import { TakeOverTypeEnum } from '@outbound-ai/softphone'
import { useAppDispatch, useAppSelector } from 'appState/store'
import { MuteUnmuteAudio } from 'components/Buttons/MuteUnmuteAudio'
import { MuteUnmuteMicrophone } from 'components/Buttons/MuteUnmuteMicrophone'
import { RefreshIntervalsEnum } from 'configs/apiConfigs'
import { useClaim } from 'features/claims/claimsHooks'
import { getClaimCallPath } from 'features/claims/utils/claimsPathUtils'
import { isEmpty } from 'lodash'
import { useEffect, useState } from 'react'
import { FaAngleRight, FaPhoneVolume } from 'react-icons/fa'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { getTntIdForNavigation } from 'utils/tenantUrlUtils'
import { toast } from 'react-toastify'
import { Tooltip } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { PositionEnum } from 'types/cssTypes'
import { formatPhoneNumber } from 'utils'
import { notificator } from 'utils/notificator/notificator'

import { ErrorMessagesEnum } from '../../../types/errorTypes'
import { useUserPreference } from '../../settings/settingsHooks'
import {
  DEFAULT_PAYER_VA_SOFTPHONE_KEY,
  IPhonePreferences,
  IUserPhone,
  UserPreferencesKeysEnum,
} from '../../settings/settingsTypes'
import { parseJSON } from '../../settings/settingsUtils'
import { useCall } from '../callsHooks'
import { updateLiveCall } from '../callsSlice'
import { CallStatusEnum, CallTypeEnum, CallTypeToJobTypeEnum } from '../types'
import { softphone } from '../utils/softphone'

export const ClaimMinimizedCallModal = () => {
  const [notificationId, setNotificationId] = useState<string | number | null>(null)
  const [audioExist, setAudioExist] = useState<boolean>(false)

  // global state
  const {
    type,
    elapsedTime,
    activeCallJobId,
    activeClaimId,
    takeOver,
    isConnected,
    isMutedAudio,
    isMuted,
    claimId,
    activeCallJbId,
    shouldRedirectIfCallTakenOver,
  } = useAppSelector((state) => state.calls.talkToPayorRepLiveCall)

  // api
  // This request will run  only when we have an activeCallJobId which guarantees that we have a talk to payer rep call in progress
  const { data: call } = useCall(activeCallJobId, {
    refreshInterval: RefreshIntervalsEnum.useCallForTalkToPayorRepCalls,
  })
  const { data: claim } = useClaim(activeClaimId)
  const { data: userPhoneData, error: userPhoneError } = useUserPreference(
    UserPreferencesKeysEnum.softphonePhoneNumbers,
  )

  // utils
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  const tenantId = getTntIdForNavigation()
  const claimCallPath = getClaimCallPath(tenantId, claim?.claimId, call?.jbId as string)

  const isLiveCallsPage = location.pathname.includes(claimCallPath)

  useEffect(() => {
    let timeOutId: ReturnType<typeof setTimeout> | null = null

    const checkAudioExist = () => {
      if (softphone.isAudioExist()) {
        setAudioExist(true)
      } else {
        timeOutId = setTimeout(() => {
          setAudioExist(false)
        }, 5000)
      }
    }
    if (activeCallJobId && call?.status === CallStatusEnum.InProgress && !audioExist) {
      checkAudioExist()
    }
    return () => {
      if (timeOutId) {
        clearTimeout(timeOutId)
      }
    }
  }, [activeCallJobId, call, audioExist])

  // effects
  useEffect(() => {
    if (userPhoneData) {
      const parsedPhoneData = parseJSON(userPhoneData.value)
      let userPhone: IUserPhone | null = null
      let preferredTakeOverType = TakeOverTypeEnum.browser
      if (!isEmpty(parsedPhoneData) && typeof parsedPhoneData === 'object') {
        const phonePreferences = parsedPhoneData as IPhonePreferences
        if (
          phonePreferences.currentSelection !== DEFAULT_PAYER_VA_SOFTPHONE_KEY &&
          phonePreferences.phones &&
          0 < phonePreferences.phones.length
        ) {
          userPhone = phonePreferences.phones.find(
            (phone) => phonePreferences.currentSelection === phone.id,
          ) as IUserPhone
          preferredTakeOverType = TakeOverTypeEnum.dialed
        }
      }
      // SOUND-CHECK: validate mic & speakers available if TakeOverTypeEnum.dialed
      softphone.updateAvailableTakeOverType(preferredTakeOverType)
      softphone.updateUserTakeOverPhone(userPhone)
    }
  }, [userPhoneData])

  useEffect(() => {
    userPhoneError &&
      notificator.notify({
        heading: userPhoneError.message || ErrorMessagesEnum.ResourceNotFound,
        type: 'error',
      }) //notificator.error(userPhoneError.message || ErrorMessagesEnum.ResourceNotFound)
  }, [userPhoneError])

  useEffect(() => {
    if (activeCallJobId && call) {
      if (call?.status === CallStatusEnum.InProgress) {
        softphone.initConnection(call)
        if (!audioExist) {
          console.log('Audio does not exist, re-initializing connection')
          softphone.initConnection(call)
        } else {
          console.log('Audio already exists, no need to re-init connection')
        }

        notificationId &&
          notificator.notify({
            heading: 'Call started successfully',
            type: 'success',
            toastOptions: { toastId: notificationId },
          })
      }

      if (call?.status === CallStatusEnum.FailedToQueue) {
        notificationId &&
          notificator.notify({
            heading: 'Call failed to queue, please try again or report the issue to the administrator',
            type: 'error',
            toastOptions: { toastId: notificationId },
          })
      }

      if (call?.status === CallStatusEnum.FailedToComplete) {
        notificationId &&
          notificator.notify({
            heading: 'Call failed to complete, please try again or report the issue to the administrator',
            type: 'error',
            toastOptions: { toastId: notificationId },
          })
      }
    }

    if (
      !call ||
      !activeCallJobId ||
      call?.status === CallStatusEnum.Complete ||
      call?.status === CallStatusEnum.FailedToComplete
    ) {
      notificationId && toast.dismiss(notificationId)
    }
  }, [call, activeCallJobId, audioExist])

  useEffect(() => {
    if (activeCallJobId && call && CallStatusEnum.Queued === call?.status) {
      const id = notificator.notify({
        type: 'loading',
        heading: 'Starting your call',
      })
      setNotificationId(id)
    } else {
      notificationId && toast.dismiss(notificationId)
    }
  }, [call, activeCallJobId, call?.status])

  const navigateToClaimCall = () => {
    console.log('Navigating to claim call page:', claimId!, activeCallJbId!)
    const tenantId = getTntIdForNavigation()
    navigate(getClaimCallPath(tenantId, claimId!, activeCallJbId!))
  }

  useEffect(() => {
    if (isConnected) {
      if (TakeOverTypeEnum.none !== takeOver.type) {
        if (TakeOverTypeEnum.browser === takeOver.type) {
          softphone.unmuteAudio()
          softphone.unmuteMicrophone()
        }
        if (
          call &&
          [CallTypeToJobTypeEnum.attendable, CallTypeToJobTypeEnum.humanAgent].includes(call.jobType) &&
          type &&
          [CallTypeEnum.attendable, CallTypeEnum.humanAgent].includes(type) &&
          shouldRedirectIfCallTakenOver
        ) {
          notificator.notify({
            heading: 'Call resumed because you are talking to a payer representative.',
            type: 'info',
            toastOptions: { toastId: 'callResumedDueToTakeOver' },
          })
          /* notificator.info('Call resumed because you are talking to a payer representative.', {
            toastId: 'callResumedDueToTakeOver',
          }) */
        }

        if (call?.jobType !== CallTypeToJobTypeEnum.diagnosticHeadset && shouldRedirectIfCallTakenOver) {
          dispatch(updateLiveCall({ shouldRedirectIfCallTakenOver: false }))
          navigateToClaimCall()
        }
      } else if (!isLiveCallsPage) {
        softphone.muteAudio()
        softphone.muteMicrophone()
      }
    }
  }, [isConnected])

  // handlers
  const handleToggleMute = () => {
    isMutedAudio ? softphone.unmuteAudio() : softphone.muteAudio()
  }

  // variables
  const isConductingPayorAgentCall =
    [CallTypeEnum.attendable, CallTypeEnum.humanAgent, CallTypeEnum.standard].includes(type!) &&
    call?.status === CallStatusEnum.InProgress
  const isHidden =
    !isConductingPayorAgentCall ||
    isLiveCallsPage ||
    call?.status === CallStatusEnum.FailedToQueue ||
    call?.status === CallStatusEnum.FailedToComplete ||
    call?.jobType === CallTypeToJobTypeEnum.diagnosticHeadset
  const payerDisplay =
    call?.jobType === CallTypeToJobTypeEnum.standard
      ? formatPhoneNumber(call?.phoneNumber || '') || call?.phoneNumber || 'Unknown'
      : claim?.insurerName || 'Unknown'

  const redirectToClaimCall = (oaiClaimId: string, jobId: string) => {
    console.log('Redirecting to claim call path:', oaiClaimId, jobId)
    const tenantId = getTntIdForNavigation()
    return getClaimCallPath(tenantId, oaiClaimId, jobId)
  }

  // early returns
  if (isHidden) return <></>
  return (
    <Div>
      <div className="call-icon">
        <FaPhoneVolume />
      </div>

      <Tooltip
        tooltipText="Go to Live Call page"
        position={PositionEnum.top}
        positionContent={60}
        positionTooltipArrow={15}
        tooltipStyles={{ 'min-width': '8.75rem' }}
      >
        <Link to={redirectToClaimCall(claimId!, activeCallJbId!)} className="call-link d-flex align-items-center gap-2">
          <div className="call-elapsed-time">{elapsedTime}</div>
          <div className="call-type">
            Contacting a <p className="truncate">{payerDisplay}</p> agent{' '}
            <FaAngleRight size={20} />
          </div>
        </Link>
      </Tooltip>

      <div className="divider" />

      <div className="d-flex align-items-center gap-2">
        <MuteUnmuteAudio isMutedAudio={isMutedAudio} toggleIsMuted={handleToggleMute} />
        {TakeOverTypeEnum.browser === takeOver.type && (
          <MuteUnmuteMicrophone isMuted={isMuted} toggleIsMuted={softphone.toggleMute} />
        )}
      </div>
    </Div>
  )
}

const Div = styled.div`
  position: absolute;
  z-index: ${({ theme }) => theme.zIndex.modal};
  bottom: 3rem;
  left: 2rem;
  transform: none;
  display: flex;
  align-items: center;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  border-radius: ${({ theme }) => theme.borderRadius.rounded};
  background-color: ${({ theme }) => theme.colors.tertiary.lighter};
  max-height: ${({ theme }) => theme.util.pixelToRem(55)};
  padding-right: 0.5rem;

  .call-icon {
    width: 56px;
    height: 56px;
    background: ${({ theme }) => theme.colors.primary.darker};
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
    border-radius: 0.625rem;
    color: ${({ theme }) => theme.colors.grey.lightest};
    margin-left: -1rem;
    z-index: 1;

    svg {
      width: ${({ theme }) => theme.util.pixelToRem(25)};
      height: ${({ theme }) => theme.util.pixelToRem(25)};
      margin: 1rem;
    }
  }

  .call-link {
    padding-block: 0.25rem;
    position: relative;
    left: -0.5rem;
    border-radius: 0px ${({ theme }) => theme.borderRadius.rounded} ${({ theme }) => theme.borderRadius.rounded} 0px;

    &:hover {
      transition: background-color 200ms ease-in, scale 200ms ease-in;
      background-color: ${({ theme }) => theme.colors.primary.lighter};
      transform: scale(1.005);
      gap: 1rem;
    }

    .call-elapsed-time {
      background-color: ${({ theme }) => theme.colors.grey.lightest};
      padding: 0.375rem 0.75rem;
      border-radius: 0px ${({ theme }) => theme.borderRadius.rounded} ${({ theme }) => theme.borderRadius.rounded} 0px;
      color: ${({ theme }) => theme.colors.primary.darker};
      z-index: 0;
    }

    .call-type {
      color: ${({ theme }) => theme.colors.primary.darker};
      font-weight: ${({ theme }) => theme.fontWeight.bold};
      display: flex;

      .truncate {
        max-width: 20ch;
        white-space: nowrap;
        overflow: hidden;
        margin-top: ${({ theme }) => theme.util.pixelToRem(2)};
        text-transform: uppercase;
        margin-left: ${({ theme }) => theme.util.pixelToRem(4)};
        margin-right: ${({ theme }) => theme.util.pixelToRem(3)};
        text-overflow: ellipsis;
      }
    }
  }

  .divider {
    width: ${({ theme }) => theme.util.pixelToRem(1)};
    height: ${({ theme }) => theme.util.pixelToRem(35)};
    flex-grow: 0;
    background-color: ${({ theme }) => theme.colors.tertiary.neutral};
    margin-right: 0.625rem;
  }
`
